/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import setIn from 'lodash/set';
import getIn from 'lodash/get';
import hasIn from 'lodash/hasIn';
import unsetIn from 'lodash/unset';

import type { FormErrors } from '../interfaces';
import type { ValidationState } from './validation-state';

type OnChangeHandler<Values> = (value: Values) => void;

export class FormState<V extends Object> {
    value: V;
    fixedStartValue: string;
    errors: FormErrors<V> = {};
    externalErrors: FormErrors<V> = {};
    validator: (V) => FormErrors<V>;
    onChangeHandler: OnChangeHandler<V>[] = [];
    validationState: ValidationState;

    constructor(validator: (V) => FormErrors<V>) {
        this.validator = validator;
    }

    handleNewValue(): void {
        this.validate();
        this.onChangeHandler.forEach(handler => handler(this.value));
    }

    setValidator(validator: (V) => FormErrors<V>) {
        this.validator = validator;
    }

    validate(): boolean {
        const internalErrors = this.validator ? this.validator(this.value) : {};
        this.errors = Object.assign(internalErrors, this.externalErrors);
        return this.isValid();
    }

    getError(path: string): any {
        return getIn(this.errors, path);
    }

    getValue(path: string): any {
        return getIn(this.value, path);
    }

    setValue(pathOrValue: string | V, value?: any) {
        if ((typeof pathOrValue === 'string')) {
            this.setValueByPath(pathOrValue, value);
        } else {
            this.mergeValue(pathOrValue);
        }
    }

    mergeValue(value: V) {
        Object.assign(this.value, value);
        this.handleNewValue();
    }

    isValid(): boolean {
        return Object.keys(this.errors).length === 0;
    }

    isDataChanged(): boolean {
        return JSON.stringify(this.value) !== this.fixedStartValue;
    }

    setValueByPath(path: string, value: Object) {
        this.checkAndResetExternalErrors(path);

        setIn(this.value, path, value);
        this.handleNewValue();
    }

    getExternalErrors(): FormErrors<V> {
        return this.externalErrors;
    }

    setExternalErrors(errors: FormErrors<V>): void {
        this.externalErrors = errors;
        this.handleNewValue();
    }

    onChange(handler: OnChangeHandler<V>): void {
        this.onChangeHandler.push(handler);
    }

    unsubscribeOnChange(handler: OnChangeHandler<V>): void {
        if (this.onChangeHandler) {
            this.onChangeHandler = this.onChangeHandler.filter(handlerItem => handlerItem !== handler);
        }
    }

    setValidationState(validationState: ValidationState): void {
        if (this.validationState !== validationState) {
            if (this.validationState) {
                console.error('Do not change the validationState object on the form, instead change the state of the original validator.');
                this.validationState.destroyObject();
            }

            this.validationState = validationState;
            this.validationState.onChange(() => {
                this.onChangeHandler.forEach(handler => handler(this.value));
            });
        }
    }

    destroyObject(): void {
        this.value = undefined;
        this.onChangeHandler = undefined;
        this.validationState?.destroyObject();
        this.validationState = undefined;
    }

    private checkAndResetExternalErrors(path: string) {
        if (hasIn(this.externalErrors, path)) {
            unsetIn(this.externalErrors, path);
        }
    }
}
